import { baseApi } from "./baseApi";

export type QuestType = "season" | "hidden";

export type CompanyQuestStatus = "in_progress" | "completed" | "claimed";
export type QuestTargetRole = "all" | "buyer" | "seller";
export type QuestConditionType = "count" | "streak";

export type BadgeTier = "gold" | "silver" | "bronze";

export interface QuestRewardBadge {
  id: string;
  code: string;
  name: string;
  tier: BadgeTier;
  /**
   * Path to the "unlocked" badge art under the FE public folder
   * (e.g. "/badge/unlocked/bdg-001.png"). Resolve with getPublicAssetUrl.
   */
  icon_url: string;
}

export interface QuestProgress {
  value: number;
  status: CompanyQuestStatus;
  period_start_date: string;
  period_end_date: string;
  completed_at: string | null;
  claimed_at: string | null;
  company_quest_id: string | null;
}

export interface QuestWithProgress {
  id: string;
  code: string;
  quest_type: QuestType;
  title: string;
  description: string | null;
  action_type: string;
  condition_type: QuestConditionType;
  target_count: number;
  reward_xp: number;
  target_role: QuestTargetRole;
  active_start_at: string | null;
  active_end_at: string | null;
  reward_badge: QuestRewardBadge | null;
  is_hidden: boolean;
  sort_order: number;
  progress: QuestProgress;
}

export interface MyQuestsResponse {
  data: QuestWithProgress[];
  message: string;
  success: boolean;
}

export interface ClaimResult {
  company_quest_id: string;
  quest_code: string;
  rewarded_xp: number;
  claimed_at: string;
}

export interface ClaimQuestResponse {
  data: ClaimResult;
  message: string;
  success: boolean;
}

/** One auto-claimed hidden-quest achievement shown in the dashboard お知らせ block. */
export interface QuestNotification {
  company_quest_id: string;
  quest_code: string;
  title: string;
  reward_xp: number;
  /** Name of the reward badge, or null when the quest grants no badge. */
  badge_name: string | null;
  claimed_at: string;
}

export interface QuestNotificationsResponse {
  data: QuestNotification[];
  message: string;
  success: boolean;
}

const MY_QUESTS_URL = "/gamification/me/quests";
const QUEST_NOTIFICATIONS_URL = "/gamification/me/quest-notifications";

export const questApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMyQuests: builder.query<MyQuestsResponse, { type?: QuestType }>({
      query: ({ type = "season" } = {}) => ({
        url: MY_QUESTS_URL,
        params: { type },
      }),
      providesTags: ["Quest"],
    }),
    claimQuest: builder.mutation<
      ClaimQuestResponse,
      { companyQuestId: string }
    >({
      query: ({ companyQuestId }) => ({
        url: `${MY_QUESTS_URL}/${companyQuestId}/claim`,
        method: "POST",
      }),
      invalidatesTags: ["Quest", "Xp", "Badge"],
    }),
    getQuestNotifications: builder.query<QuestNotificationsResponse, void>({
      query: () => ({ url: QUEST_NOTIFICATIONS_URL }),
      providesTags: ["Quest"],
    }),
  }),
});

export const {
  useGetMyQuestsQuery,
  useClaimQuestMutation,
  useGetQuestNotificationsQuery,
} = questApi;
